import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@pps/ui/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { usePlanFeatures } from '@/hooks/usePlanFeatures';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "@pps/ui/hooks/use-toast";
import { Check, Crown, Zap, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { resolvePlanAction } from '@/lib/plan-action';
import { edgeErrorMessage } from '@/lib/edge-error-message';

interface PlanUpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PlanData {
  id: string;
  name: string;
  icon: typeof Zap;
  price: string;
  description: string;
  features: string[];
  inheritLabel?: string;
  tier: string;
}

// Catálogo enxuto (20260703150000_remove_pro_tier.sql): só free e
// enterprise — as features do ex-Pro foram absorvidas pelo Enterprise.
export function PlanUpgradeDialog({ open, onOpenChange }: PlanUpgradeDialogProps) {
  const { t } = useTranslation();
  const { club } = useAuth();
  const { tier } = usePlanFeatures();
  const [loading, setLoading] = useState<string | null>(null);

  const buildStaticPlans = (): PlanData[] => [
    {
      id: 'free',
      tier: 'free',
      name: 'Free',
      icon: Zap,
      price: t('common.free'),
      description: '',
      features: [
        'CRM · ' + t('planUpgrade.upTo50Leads', 'até 50 leads'),
        t('nav.agenda'),
        t('nav.courts'),
        t('nav.categories'),
        t('nav.tournaments'),
      ],
    },
    {
      id: 'enterprise',
      tier: 'enterprise',
      name: 'Enterprise',
      icon: Crown,
      price: '',
      description: '',
      inheritLabel: t('planUpgrade.everythingInFree', 'Tudo do Free +'),
      features: [
        t('planUpgrade.unlimitedCRM', 'CRM ilimitado'),
        t('nav.opportunities'),
        t('planUpgrade.academy', 'Academia'),
        t('planUpgrade.plansAndInvoices', 'Planos e Faturas'),
        t('nav.commissions'),
        t('planUpgrade.paymentMethods', 'Métodos de Pagamento'),
        t('nav.expenses'),
        'NFS-e',
        t('nav.financialPanel'),
        'WhatsApp e Instagram',
        t('planUpgrade.integrations', 'Integrações (Wellhub, TotalPass)'),
        t('planUpgrade.apiAndSite', 'API e Site Público'),
        t('planUpgrade.prioritySupport', 'Suporte prioritário'),
      ],
    },
  ];

  const [plans, setPlans] = useState<PlanData[]>([]);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFetching(true);
    const staticPlans = buildStaticPlans();

    supabase
      .from('club_plan_prices')
      .select('*')
      .eq('is_active', true)
      .order('price_monthly', { ascending: true })
      .then(({ data, error }) => {
        if (error || !data || data.length === 0) {
          setPlans(staticPlans);
          setFetching(false);
          return;
        }

        // Merge DB prices into static plans
        const merged = staticPlans.map(sp => {
          const dbRow = data.find(r => r.plan_tier === sp.tier);
          if (dbRow) {
            return {
              ...sp,
              id: dbRow.id,
              name: dbRow.name || sp.name,
              price: dbRow.price_monthly > 0
                ? `R$ ${Number(dbRow.price_monthly).toLocaleString('pt-BR')}/mês`
                : sp.price,
              description: dbRow.description || sp.description,
            };
          }
          return sp;
        });

        setPlans(merged);
        setFetching(false);
      });
  }, [open]);

  async function handleSelectPlan(planTier: string) {
    if (!club || planTier === tier) return;
    setLoading(planTier);
    try {
      // P0-6: toda troca de plano passa por edge function autenticada (Stripe),
      // NUNCA por UPDATE direto em clubs.plan_tier (upgrade sem pagar).
      const action = resolvePlanAction({
        clubId: club.id,
        currentTier: tier,
        targetTier: planTier,
        returnUrl: window.location.origin,
      });
      if (action.kind === 'noop') return;

      const { data, error } = await supabase.functions.invoke(action.fn, {
        body: action.body,
      });
      // Sem isto o usuário via só "Edge Function returned a non-2xx status
      // code" (message crua do FunctionsHttpError) — o code tipado do payload
      // fica dentro de `error.context`.
      if (error) throw new Error(await edgeErrorMessage(error));

      // Upgrade pago → redireciona para o Stripe Checkout.
      if (action.kind === 'upgrade' && data?.url) {
        window.location.href = data.url as string;
        return;
      }

      // Upgrade por cupom 100% (data.activated) ou downgrade (data.success).
      const planName = plans.find(p => p.tier === planTier)?.name;
      toast({ title: `${t('planUpgrade.title')}: ${planName}!` });
      onOpenChange(false);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: t('common.error'), description: message, variant: 'destructive' });
    } finally {
      setLoading(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-xl">{t('planUpgrade.title')}</DialogTitle>
          <DialogDescription>{t('planUpgrade.selectPlan')}</DialogDescription>
        </DialogHeader>
        {fetching ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            {plans.map(plan => {
              const isCurrent = plan.tier === tier;
              const Icon = plan.icon;
              return (
                <div
                  key={plan.tier}
                  className={`border rounded-xl p-5 flex flex-col gap-4 transition-all ${
                    isCurrent
                      ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-base">{plan.name}</span>
                    {isCurrent && (
                      <Badge variant="secondary" className="text-[10px] ml-auto">{t('planUpgrade.currentPlan')}</Badge>
                    )}
                  </div>
                  {plan.price && <div className="text-2xl font-bold">{plan.price}</div>}
                  {plan.description && (
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  )}
                  <ul className="space-y-2 text-sm flex-1">
                    {plan.inheritLabel && (
                      <li className="font-medium text-primary mb-1">{plan.inheritLabel}</li>
                    )}
                    {plan.features.map(f => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={isCurrent ? 'outline' : 'default'}
                    disabled={isCurrent || loading !== null}
                    onClick={() => handleSelectPlan(plan.tier)}
                    className="w-full"
                  >
                    {loading === plan.tier ? t('common.loading') : isCurrent ? t('planUpgrade.currentPlan') : t('planUpgrade.upgrade')}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
